import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';

import { request } from '../helpers/requests';

function useProjectOrGlobalCostcodes({
  projectId,
  divisionId,
  divisionIds,
  visible,
  includeCategoryIds = false,
  columns = [],
}) {
  const resolvedDivisionIds = useMemo(() => {
    if (divisionIds?.length) return divisionIds;
    if (divisionId) return [divisionId];
    return [];
  }, [divisionId, divisionIds]);

  const divisionIdsKey = useMemo(
    () => [...resolvedDivisionIds]
      .filter(Boolean)
      .sort((a, b) => String(a).localeCompare(String(b)))
      .join(','),
    [resolvedDivisionIds],
  );

  const columnsKey = useMemo(
    () => [...(columns || [])]
      .sort((a, b) => String(a).localeCompare(String(b)))
      .join(','),
    [columns],
  );

  const shouldFetch = Boolean(visible && (projectId || divisionIdsKey));

  const [costcodes, setCostcodes] = useState([]);
  // True on first paint when a fetch will run so costcodesLoading does not briefly
  // look "done" before the effect starts.
  const [loading, setLoading] = useState(shouldFetch);

  useEffect(() => {
    if (!shouldFetch) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;

    const getCostcodes = async () => {
      setLoading(true);
      const res = await request({
        call: projectId
          ? axios.post('/costcodes/projects', { projectIds: [projectId], includeCategoryIds })
          : axios.get('/costcodes/global', {
            params: {
              divisionIds: divisionIdsKey.split(','),
              includeCategoryIds,
              ...(columnsKey && { columns: columnsKey.split(',') }),
            },
          }),
        errMsg: 'Could not get costcodes',
        hideSuccessToast: true,
      });
      if (cancelled) return;
      setCostcodes(res?.data ?? []);
      setLoading(false);
    };
    getCostcodes();

    return () => {
      cancelled = true;
    };
  }, [
    shouldFetch,
    projectId,
    divisionIdsKey,
    includeCategoryIds,
    columnsKey,
  ]);
  return { costcodes, loading };
}

export default useProjectOrGlobalCostcodes;
